import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaPlus, FaTrash, FaUserClock } from 'react-icons/fa';
import customerWaitlistService from '../../utils/customerWaitlistService';

function matchesQuery(entry, query) {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  if (String(entry.name || '').toLowerCase().includes(q)) return true;
  if (String(entry.email || '').toLowerCase().includes(q)) return true;
  if (String(entry.phone || '').toLowerCase().includes(q)) return true;
  if (String(entry.address || '').toLowerCase().includes(q)) return true;
  if (String(entry.productName || '').toLowerCase().includes(q)) return true;
  return false;
}

const CustomerWaitlistList = () => {
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [query, setQuery] = useState('');

  useEffect(() => {
    setEntries(customerWaitlistService.getAll());
  }, []);

  const refresh = () => setEntries(customerWaitlistService.getAll());

  const filtered = useMemo(() => {
    return [...entries]
      .filter((e) => matchesQuery(e, query))
      .sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime());
  }, [entries, query]);

  const totalCustomers = entries.length;
  const totalRequested = entries.reduce((sum, e) => sum + (Number(e.requiredQuantity) || 0), 0);

  const handleAdd = () => navigate('/dashboard/customers/new');
  const handleDelete = (entry) => {
    if (window.confirm(`Remove ${entry.name} from waitlist?`)) {
      customerWaitlistService.remove(entry.id);
      refresh();
    }
  };

  return (
    <div>
      <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div className="text-left">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-emerald-700">
            Waitlist
          </p>
          <p className="mt-2 text-sm text-slate-600">
            Customers: <span className="font-semibold text-slate-900">{totalCustomers}</span>{' '}
            <span className="text-slate-400">|</span>{' '}
            Items requested: <span className="font-semibold text-slate-900">{totalRequested}</span>
          </p>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, email, phone, product..."
            className="w-full rounded-md border border-slate-200 bg-white px-4 py-3 text-sm shadow-sm focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-200 sm:w-[320px]"
          />
          <button
            onClick={handleAdd}
            className="inline-flex items-center justify-center gap-2 rounded-md bg-[#00595A] px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-[#004446]"
          >
            <FaPlus className="h-4 w-4" />
            Add Customer
          </button>
        </div>
      </div>

      <div className="hidden overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm md:block">
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Customer</th>
              <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Phone</th>
              <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Email</th>
              <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Product</th>
              <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Qty</th>
              <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {filtered.map((entry) => (
              <tr key={entry.id} className="hover:bg-slate-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-slate-900">{entry.name}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">{entry.phone || '-'}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">{entry.email || '-'}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-700">{entry.productName}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-slate-900">{entry.requiredQuantity}</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <button
                    onClick={() => handleDelete(entry)}
                    className="inline-flex items-center gap-2 rounded-md bg-rose-100 px-3 py-2 text-sm font-semibold text-rose-800 transition hover:bg-rose-200"
                  >
                    <FaTrash className="h-4 w-4" />
                    Delete
                  </button>
                </td>
              </tr>
            ))}

            {!filtered.length ? (
              <tr>
                <td colSpan={6} className="px-6 py-10 text-center text-sm text-slate-500">
                  No customers in waitlist.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <div className="grid grid-cols-1 gap-3 md:hidden">
        {filtered.map((entry) => (
          <article key={entry.id} className="rounded-lg border border-slate-200 bg-white p-4 text-left shadow-sm">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-emerald-700">
                  Waitlist Entry
                </p>
                <h3 className="mt-2 text-base font-semibold text-slate-900">{entry.name}</h3>
                <p className="mt-1 text-sm text-slate-500">{entry.phone || '-'}</p>
                <p className="mt-1 text-sm text-slate-500">{entry.email || '-'}</p>
              </div>
              <span className="grid h-10 w-10 place-items-center rounded-md bg-slate-100 text-slate-700">
                <FaUserClock className="h-4 w-4" />
              </span>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-2 text-sm">
              <div className="rounded-md bg-slate-50 px-3 py-2">
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Product</p>
                <p className="mt-1 font-semibold text-slate-800">{entry.productName}</p>
              </div>
              <div className="rounded-md bg-slate-50 px-3 py-2">
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Qty</p>
                <p className="mt-1 font-semibold text-slate-800">{entry.requiredQuantity}</p>
              </div>
            </div>

            <button
              onClick={() => handleDelete(entry)}
              className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-md bg-rose-100 px-4 py-3 text-sm font-semibold text-rose-800 transition hover:bg-rose-200"
            >
              <FaTrash className="h-4 w-4" />
              Delete
            </button>
          </article>
        ))}

        {!filtered.length ? (
          <div className="rounded-lg border border-slate-200 bg-white p-6 text-left text-sm text-slate-500 shadow-sm">
            No customers in waitlist.
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default CustomerWaitlistList;
